import React, { useEffect, useState, useCallback } from "react";
import { getUsersDbList } from "../api/clientDbAppUsers";
import { userId, userClientDb } from "../auth/selectors/userSelector";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { setUserDbId } from "../auth/slices/userSlice";
import { useLocalStorage, useAuthorizationError, useLoader } from "../../hooks";
import { ItemsList, Alert } from "../../components";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "../../components/form/formUtils";
import { LoginLayout } from "../../layouts";
import { Navigate } from "react-router-dom";
import * as Routes from "../../app/routes";
import DbLogo from "../../logo/database-light1.svg";
import DbLogoDark from "../../logo/database-dark1.svg";
import { useColorScheme } from "@mui/material/styles";

function CompaniesList() {
  const [translate] = useTranslation();
  const user = useAppSelector(userId);
  const userDb = useAppSelector(userClientDb);
  const [logoutUser] = useAuthorizationError();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<Array<{ value: number; label: string }>>();
  const [localStorage, setLocalStorage] = useLocalStorage("userDb");
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const [, setLoader] = useLoader();
  const [redirect, setRedirect] = useState(false);
  const { mode } = useColorScheme();

  const assignDb = React.useCallback(
    (selectedValue: number) => {
      const selectedDb =
        data &&
        data
          .filter((item) => item.value && item.value === selectedValue)
          .map((item) => {
            return { id: item.value, name: item.label };
          });

      if (selectedDb && selectedDb[0]) {
        const isUser =
          localStorage && Array.isArray(localStorage)
            ? localStorage.filter((person: any) => person.userId === user)
            : [];

        if (isUser.length === 0) {
          if (localStorage && localStorage.length > 0) {
            const newStorage = [
              ...localStorage,
              { ...selectedDb[0], userId: user },
            ];
            setLocalStorage(newStorage);
          } else {
            setLocalStorage([{ ...selectedDb[0], userId: user }]);
          }

          dispatch(setUserDbId({ ...selectedDb[0], userId: user ? user : "" }));
        } else {
          dispatch(setUserDbId({ ...selectedDb[0], userId: user ? user : "" }));

          const newStorage = localStorage.filter(
            (person: any) => person.userId !== user
          );
          setLocalStorage([...newStorage, { ...selectedDb[0], userId: user }]);
        }

        setRedirect(true);
      }
    }, // eslint-disable-next-line
    [dispatch, data, setLocalStorage]
  );

  useEffect(
    () => {
      if (user) {
        setLoader(true);
        getUsersDbList(user)
          .then(({ data }) => {
            setLoader(false);

            const dbList = data.map((item: any) => {
              return {
                value: item.clientDbId,
                label: item.companyName || item.clientDbName,
              };
            });
            setData(dbList);
          })
          .catch((reason) => {
            setLoader(false);
            const errMessage = getErrorMessage(reason);
            setError({
              error: true,
              message: errMessage.message,
              status: errMessage.status,
            });
          });
      }
    }, // eslint-disable-next-line
    []
  );

  useEffect(
    () => {
      if (!userDb && data && data.length === 1) assignDb(data[0].value);
    }, // eslint-disable-next-line
    [data]
  );

  useEffect(
    () => {
      if (userDb && data) {
        assignDb(userDb.id);
      }
    }, // eslint-disable-next-line
    [data, userDb]
  );

  const handleErrorClose = () => {
    logoutUser(error.status);

    if (error.status !== 401)
      setError({ error: false, message: "", status: 0 });
  };

  const handleItemClick = useCallback(
    (db: number) => {
      assignDb(db);
    },
    [assignDb]
  );

  return (
    <LoginLayout>
      {error && (
        <Alert
          isOpen={error.error}
          message={error.message}
          iconColor="error"
          onClose={handleErrorClose}
        />
      )}

      {data && (
        <ItemsList
          title={translate("selectDatabase", "Pasirinkite duomenų bazę")}
          data={data}
          onItemClick={handleItemClick}
          logoSrc={mode === "dark" ? DbLogoDark : DbLogo}
        />
      )}
      {redirect && <Navigate to={Routes.ROUTE_DEPARTMENTS.path} />}
    </LoginLayout>
  );
}

export default CompaniesList;
